import { useState, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { message, Upload } from "antd";
import { ArrowLeftOutlined, SaveOutlined, UploadOutlined } from "@ant-design/icons";
import { PageShell, PageHeader, Panel } from "../components/Page";
import {
  fetchInvoiceSettings,
  updateInvoiceSettings,
  fetchInvoiceTemplates,
  uploadMedia,
} from "../api/auth";
import type { InvoiceSettings, InvoiceTemplate } from "../api/auth";

export default function InvoiceSettingsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const { data: settings, isLoading: settingsLoading, error: settingsError } = useQuery({
    queryKey: ["invoice-settings"],
    queryFn: () => fetchInvoiceSettings(),
  });

  const { data: templates, isLoading: templatesLoading } = useQuery({
    queryKey: ["invoice-templates"],
    queryFn: () => fetchInvoiceTemplates(),
  });

  const [form, setForm] = useState<Partial<InvoiceSettings>>({});

  const current: InvoiceSettings = useMemo(
    () => ({
      businessName: settings?.businessName ?? "",
      businessAddress: settings?.businessAddress ?? "",
      phoneNumber: settings?.phoneNumber ?? "",
      email: settings?.email ?? "",
      logoUrl: settings?.logoUrl ?? "",
      primaryColor: settings?.primaryColor ?? "#1a365d",
      fontFamily: settings?.fontFamily ?? "Inter",
      termsAndConditions: settings?.termsAndConditions ?? "",
      website: settings?.website ?? "",
      facebook: settings?.facebook ?? "",
      twitter: settings?.twitter ?? "",
      youtube: settings?.youtube ?? "",
      tiktok: settings?.tiktok ?? "",
      whatsapp: settings?.whatsapp ?? "",
      templateId: settings?.templateId ?? "",
      salesDefaults: settings?.salesDefaults ?? undefined,
    }),
    [settings],
  );

  const templateOptions = useMemo<InvoiceTemplate[]>(() => {
    const defaultOptions: InvoiceTemplate[] = [
      { id: "", name: "Default (Clean)" },
      { id: "simple", name: "Simple" },
      { id: "detailed", name: "Detailed" },
    ];
    return [...defaultOptions, ...(templates ?? [])];
  }, [templates]);

  const handleChange = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onLogoUpload = async (info: { fileList: { originFileObj?: File }[] }) => {
    const file = info.fileList[0]?.originFileObj;
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      message.error("Please upload an image file");
      return;
    }
    setUploading(true);
    try {
      const asset = await uploadMedia(file);
      setLogoPreview(asset.url);
      handleChange("logoUrl", asset.url);
      message.success("Logo uploaded");
    } catch (err) {
      message.error(
        (err as { message?: string })?.message ?? "Failed to upload logo",
      );
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    try {
      await updateInvoiceSettings({ ...current, ...form });
      message.success("Invoice settings saved");
    } catch (err) {
      message.error(
        (err as { message?: string })?.message ?? "Failed to save invoice settings",
      );
    } finally {
      setLoading(false);
    }
  }, [current, form]);

  if (settingsError) {
    return (
      <PageShell>
        <PageHeader
          eyebrow="Sale"
          title="Invoice Settings"
          description="Set the branding that appears on sale invoices."
          actions={
            <button
              onClick={() => navigate("/sales/manage")}
              className="inline-flex items-center gap-1 rounded-lg px-4 py-2 text-sm font-medium border border-line text-ink-muted hover:bg-sunken"
            >
              <ArrowLeftOutlined /> Back
            </button>
          }
        />
        <Panel title="Invoice Settings">
          <div className="surface-body p-6 bg-stone-50 border border-stone-200 rounded-lg">
            <div className="text-stone-700 font-medium mb-2">Failed to load settings</div>
            <div className="text-sm text-stone-600">
              {(settingsError as { message?: string })?.message ?? "Unknown error"}
            </div>
          </div>
        </Panel>
      </PageShell>
    );
  }

  if (settingsLoading || templatesLoading) {
    return (
      <PageShell>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary"></div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader
        eyebrow="Sale"
        title="Invoice Settings"
        description="Set the branding — logo, business name, contact details and terms — that appears on every sale invoice."
        actions={
          <button
            onClick={() => navigate("/sales/manage")}
            className="inline-flex items-center gap-1 rounded-lg px-4 py-2 text-sm font-medium border border-line text-ink-muted hover:bg-sunken"
          >
            <ArrowLeftOutlined /> Back
          </button>
        }
      />

      <Panel title="Business Identity" bodyClassName="p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="label">Business name</label>
            <input
              id="businessName"
              className="field"
              defaultValue={current.businessName}
              onChange={(e) => handleChange("businessName", e.target.value)}
            />
            <p className="mt-1.5 text-xs text-ink-muted">Shown as the seller brand on the invoice letterhead.</p>
          </div>
          <div>
            <label className="label">Business address</label>
            <input
              id="businessAddress"
              className="field"
              defaultValue={current.businessAddress}
              onChange={(e) => handleChange("businessAddress", e.target.value)}
            />
            <p className="mt-1.5 text-xs text-ink-muted">Printed on the invoice letterhead below the business name.</p>
          </div>
          <div>
            <label className="label">Phone number</label>
            <input
              id="phoneNumber"
              className="field"
              defaultValue={current.phoneNumber}
              onChange={(e) => handleChange("phoneNumber", e.target.value)}
            />
            <p className="mt-1.5 text-xs text-ink-muted">Printed on the invoice letterhead.</p>
          </div>
          <div>
            <label className="label">Email address</label>
            <input
              id="email"
              type="email"
              className="field"
              defaultValue={current.email}
              onChange={(e) => handleChange("email", e.target.value)}
            />
            <p className="mt-1.5 text-xs text-ink-muted">Used as the invoice from-address.</p>
          </div>
          <div className="sm:col-span-2 lg:col-span-4">
            <label className="label">Website</label>
            <input
              id="website"
              type="url"
              className="field"
              defaultValue={current.website}
              onChange={(e) => handleChange("website", e.target.value)}
            />
            <p className="mt-1.5 text-xs text-ink-muted">Optional. Printed on the invoice letterhead.</p>
          </div>
        </div>
      </Panel>

      <Panel title="Invoice Branding" bodyClassName="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="label">Logo image</label>
              <div className="flex flex-col items-center gap-3 p-4 border-2 border-dashed border-line rounded-lg hover:border-primary hover:bg-primary-wash transition-colors">
                {logoPreview ? (
                  <div className="relative">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="w-32 h-32 object-contain bg-white p-2 rounded-lg shadow-sm border border-line"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setLogoPreview(null);
                        handleChange("logoUrl", "");
                      }}
                      className="absolute -top-2 -right-2 p-1 text-red-500 hover:text-red-700"
                    >
                      <UploadOutlined className="w-5 h-5" />
                    </button>
                  </div>
                ) : current.logoUrl ? (
                  <div className="relative">
                    <img
                      src={current.logoUrl}
                      alt="Logo preview"
                      className="w-32 h-32 object-contain bg-white p-2 rounded-lg shadow-sm border border-line"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setLogoPreview(null);
                        handleChange("logoUrl", "");
                      }}
                      className="absolute -top-2 -right-2 p-1 text-red-500 hover:text-red-700"
                    >
                      <UploadOutlined className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2 text-ink-muted">
                    <UploadOutlined className="w-12 h-12 text-ink-faint" />
                    <span className="text-sm font-medium">Click to upload logo</span>
                    <p className="text-xs text-ink-faint">PNG or JPG, max 2MB</p>
                  </div>
                )}
                <Upload
                  action="/media/upload"
                  listType="text"
                  showUploadList={false}
                  name="file"
                  data={{}}
                  onChange={onLogoUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  className={cn(
                    "w-full flex items-center justify-center gap-2 rounded-lg border border-line text-sm font-medium px-4 py-2 text-ink",
                    uploading ? "opacity-50 cursor-not-allowed" : "hover:bg-sunken hover:border-primary",
                  )}
                  disabled={uploading}
                >
                  {uploading ? "Uploading…" : "Choose logo file"}
                </button>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="label">Primary color</label>
              <div className="flex items-center gap-3">
                <input
                  id="primaryColor"
                  type="color"
                  defaultValue={current.primaryColor}
                  className="h-10 w-20 rounded-lg border border-line cursor-pointer"
                  onChange={(e) => handleChange("primaryColor", e.target.value)}
                />
                <span className="text-sm text-ink-muted">Accent colour used on invoice letterhead</span>
              </div>
            </div>
            <div>
              <label className="label">Font family</label>
              <select
                id="fontFamily"
                className="field w-full"
                defaultValue={current.fontFamily}
                onChange={(e) => handleChange("fontFamily", e.target.value)}
              >
                <option value="Inter">Inter</option>
                <option value="Segoe UI">Segoe UI</option>
                <option value="Roboto">Roboto</option>
                <option value="Calibri">Calibri</option>
              </select>
              <p className="mt-1 text-xs text-ink-muted">Fonts used for invoice text.</p>
            </div>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <label className="label">Terms &amp; Conditions</label>
          <textarea
            id="termsAndConditions"
            className="field"
            defaultValue={current.termsAndConditions ?? "All sales are final. Vehicle sold as-is unless otherwise specified in the purchase agreement."}
            rows={6}
            onChange={(e) => handleChange("termsAndConditions", e.target.value)}
          />
          <p className="mt-1 text-xs text-ink-muted">Shown at the bottom of every sale invoice.</p>
        </div>
      </Panel>

      <Panel title="Social &amp; Contact" bodyClassName="p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label">Facebook URL</label>
            <input
              id="facebook"
              type="url"
              className="field w-full"
              defaultValue={current.facebook}
              onChange={(e) => handleChange("facebook", e.target.value)}
            />
          </div>
          <div>
            <label className="label">Twitter / X URL</label>
            <input
              id="twitter"
              type="url"
              className="field w-full"
              defaultValue={current.twitter}
              onChange={(e) => handleChange("twitter", e.target.value)}
            />
          </div>
          <div>
            <label className="label">YouTube URL</label>
            <input
              id="youtube"
              type="url"
              className="field w-full"
              defaultValue={current.youtube}
              onChange={(e) => handleChange("youtube", e.target.value)}
            />
          </div>
          <div>
            <label className="label">WhatsApp number</label>
            <input
              id="whatsapp"
              className="field w-full"
              defaultValue={current.whatsapp}
              onChange={(e) => handleChange("whatsapp", e.target.value)}
            />
          </div>
        </div>
      </Panel>

      <Panel title="Invoice Template" bodyClassName="p-6">
        <div className="flex items-center gap-4">
          <label className="label">Template</label>
          <select
            className="field w-80"
            defaultValue={current.templateId ?? ""}
            onChange={(e) => handleChange("templateId", e.target.value as string)}
          >
            {templateOptions.map((opt) => (
              <option key={opt.id} value={opt.id ?? ""}>
                {opt.name}
              </option>
            ))}
          </select>
          <p className="text-xs text-ink-muted">Leave blank to use the default template.</p>
        </div>
      </Panel>

      <Panel title="Sale Defaults" bodyClassName="p-6">
        <p className="mb-4 text-xs text-ink-muted">
          Pre-populated on every new sale unless overridden by the dealer.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label">Default Discount %</label>
            <input
              id="defaultDiscountPercent"
              type="number"
              min="0"
              max="100"
              step="0.01"
              className="field w-full"
              defaultValue={current.salesDefaults?.defaultDiscountPercent != null ? String(current.salesDefaults.defaultDiscountPercent) : ""}
              onChange={(e) => handleChange("defaultDiscountPercent", e.target.value)}
            />
            <p className="mt-1 text-xs text-ink-muted">Pre-populated discount percent on new sales.</p>
          </div>
          <div className="sm:col-span-2">
            <label className="label">Default Charges (JSON)</label>
            <textarea
              id="defaultCharges"
              className="field w-full font-mono text-sm"
              defaultValue={JSON.stringify(current.salesDefaults?.defaultCharges ?? [], null, 2)}
              rows={4}
              onChange={(e) => handleChange("defaultCharges", e.target.value)}
            />
            <p className="mt-1 text-xs text-ink-muted">
              Array of charge objects: kind, label, amount, type, ratePercent, baseAmount, costAmount.
            </p>
          </div>
          <div>
            <label className="label">Default Payment Account</label>
            <input
              id="defaultPaymentAccountId"
              className="field w-full"
              defaultValue={current.salesDefaults?.defaultPaymentAccountId ?? ""}
              onChange={(e) => handleChange("defaultPaymentAccountId", e.target.value)}
            />
            <p className="mt-1 text-xs text-ink-muted">Used when no first payment amount is given.</p>
          </div>
          <div className="sm:col-span-2">
            <label className="label">Default Notes</label>
            <input
              id="defaultNotes"
              className="field w-full"
              defaultValue={current.salesDefaults?.defaultNotes ?? ""}
              onChange={(e) => handleChange("defaultNotes", e.target.value)}
            />
            <p className="mt-1 text-xs text-ink-muted">Printed on every invoice.</p>
          </div>
        </div>
      </Panel>

      <div className="mt-8 flex justify-end">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg bg-primary text-white px-6 py-2.5 text-sm font-medium hover:bg-primary-deep disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? "Saving…" : <><SaveOutlined /> Save Invoice Settings</>}
        </button>
      </div>
    </PageShell>
  );
}

function cn(...classes: (string | undefined | null | boolean)[]): string {
  return classes.filter(Boolean).join(" ");
}